import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { POA_FAQ_CATEGORIES } from "@/content/poa-corpus/faq-defs";
import { POA_JURISDICTION_NAMES as POA_JURISDICTIONS } from "@/content/poa-corpus/names.gen";

const ADVICE_TH = [
  "ขอแบบฟอร์มจากหน่วยงานผู้รับก่อนร่างเอกสารเสมอ กรมที่ดินใช้แบบ ท.ด.21 ธนาคารและสถานทูตส่วนใหญ่มีแบบของตนเองที่ไม่รับแบบทั่วไป",
  "ระบุขอบเขตอำนาจให้แคบและเจาะจง เช่น เลขโฉนดที่ดิน เลขห้องชุด เลขบัญชี หรือชื่อคดี เพื่อป้องกันการใช้อำนาจเกินเจตนา",
  "ตรวจว่าผู้มอบอำนาจมีอำนาจลงนามจริง กรณีนิติบุคคลต้องตรงกับกรรมการผู้มีอำนาจในหนังสือรับรองของกรมพัฒนาธุรกิจการค้าที่คัดไม่เกิน 6 เดือน",
  "ติดอากรแสตมป์ตามบัญชีอัตราอากรแสตมป์ของกรมสรรพากร ใบมอบอำนาจให้บุคคลเดียวทำการครั้งเดียวและกรณีมอบหลายคนหรือหลายครั้งใช้อัตราต่างกัน",
  "ลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร พร้อมพยานสองคน และแนบสำเนาบัตรประชาชนหรือหนังสือเดินทางที่รับรองสำเนาถูกต้องของทุกฝ่าย",
  "หากจะใช้ต่างประเทศ ให้วางลำดับรับรองไว้ก่อน คือ รับรองลายมือชื่อ → รับรองคำแปล → กองสัญชาติและนิติกรณ์ กรมการกงสุล → สถานทูตปลายทาง และเผื่อเวลาคิวของแต่ละขั้น",
  "เอกสารที่ลงนามในต่างประเทศเพื่อใช้ในไทย ต้องผ่านการรับรองที่ประเทศต้นทางและแปลเป็นไทยพร้อมรับรองคำแปลก่อนยื่นต่อสำนักงานที่ดิน ธนาคาร หรือศาล",
  "เตรียมต้นฉบับสำรองอย่างน้อยหนึ่งชุด เพราะสำนักงานที่ดินและธนาคารมักเก็บต้นฉบับไว้ในสำนวน",
];

const CAUTIONS_TH = [
  "เว้นช่องว่างหรือขีดฆ่าโดยไม่ลงนามกำกับ เป็นเหตุที่สำนักงานที่ดินและธนาคารปฏิเสธมากที่สุด ทุกช่องที่ไม่ใช้ต้องขีดฆ่าและลงนามกำกับ",
  "ใช้หนังสือมอบอำนาจทั่วไปไปทำนิติกรรมที่ดิน ทั้งที่การจดทะเบียนสิทธิและนิติกรรมต้องใช้แบบ ท.ด.21 ที่ระบุประเภทนิติกรรมและเลขโฉนดชัดเจน",
  "ลายมือชื่อไม่เหมือนตัวอย่างในบัตรประชาชน หนังสือเดินทาง หรือตัวอย่างลายมือชื่อที่ให้ธนาคารไว้ ทำให้ถูกตีกลับแม้เอกสารถูกต้องทุกอย่าง",
  "หนังสือมอบอำนาจเก่าเกินกรอบที่หน่วยงานยอมรับ หลายหน่วยงานขอเอกสารที่ลงนามไม่เกิน 3–6 เดือน ทั้งที่กฎหมายไม่ได้กำหนดอายุไว้",
  "ไม่ติดอากรแสตมป์หรือติดไม่ครบตามอัตราของกรมสรรพากร ทำให้ใช้เป็นพยานหลักฐานได้ยากและถูกทักท้วงในชั้นเจ้าหน้าที่",
  "การมอบอำนาจสิ้นผลเมื่อผู้มอบอำนาจถึงแก่ความตายหรือถูกศาลสั่งให้เป็นคนไร้ความสามารถ การใช้เอกสารต่อหลังจากนั้นเป็นความเสี่ยงทางกฎหมายของผู้รับมอบอำนาจ",
  "แปลเอกสารโดยไม่ผ่านการรับรองคำแปลที่กรมการกงสุล ทั้งที่หน่วยงานปลายทางกำหนดให้คำแปลต้องมีตรานิติกรณ์",
  "ลืมเผื่อว่าอนุสัญญา Apostille จะมีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570 เอกสารที่ยื่นก่อนวันนั้นยังต้องใช้ลำดับรับรองสถานทูตปลายทางตามเดิม",
];

export const Route = createFileRoute("/poa/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/poa-corpus");
    return { faqs: m.POA_FAQ_TH };
  },
  component: PoaFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `หนังสือมอบอำนาจข้ามประเทศ — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องหนังสือมอบอำนาจ: แบบ ท.ด.21 ของกรมที่ดิน มอบอำนาจธนาคาร มรดก นิติบุคคล หนังสือยินยอมบุตร การรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรอง นิติกรณ์กรมการกงสุล สถานทูต และ Apostille ที่มีผลกับไทย 28 ก.พ. 2570",
        path: "/poa/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/poa/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ หนังสือมอบอำนาจข้ามประเทศ", url: `${site}/poa/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/poa/faq")) },
      ],
    };
  },
});

function PoaFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ หนังสือมอบอำนาจข้ามประเทศ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          หนังสือมอบอำนาจส่วนบุคคลและธุรกรรมข้ามประเทศ — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          หนังสือมอบอำนาจคือเอกสารที่ผู้มอบอำนาจตั้งตัวแทนให้ทำการแทนตามประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 3 ลักษณะ 15
          ตัวแทน มาตรา 797–828 การใช้ในไทยต้องเลือกแบบให้ตรงหน่วยงาน เช่น แบบ ท.ด.21 ของกรมที่ดินสำหรับนิติกรรมที่ดิน
          ติดอากรแสตมป์ตามบัญชีอัตราอากรแสตมป์ของกรมสรรพากร และหากจะใช้ต่างประเทศต้องผ่านการรับรองลายมือชื่อโดยทนายความ
          ผู้ทำคำรับรองลายมือชื่อและเอกสารตามข้อบังคับสภาทนายความ แล้วรับรองที่กองสัญชาติและนิติกรณ์ กรมการกงสุล
          ก่อนรับรองที่สถานทูตปลายทาง ทั้งนี้อนุสัญญา Apostille จะมีผลใช้บังคับกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570
          ข้อมูลนี้เป็นข้อมูลทั่วไป เงื่อนไขและระยะเวลาเปลี่ยนแปลงได้ โปรดยืนยันกับหน่วยงานปลายทางก่อนยื่นทุกครั้ง
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/poa/faq" className="text-primary underline">
            Power of attorney in Thailand FAQ →
          </a>
        </div>

        <section aria-label="ข้อเท็จจริงหลัก" className="mb-8 overflow-x-auto">
          <h2 className="mb-3 text-xl font-bold">ข้อเท็จจริงหลักของหนังสือมอบอำนาจไทย</h2>
          <table className="w-full border-collapse text-sm">
            <tbody>
              {[
                ["กฎหมายหลัก", "ป.พ.พ. บรรพ 3 ลักษณะ 15 ตัวแทน มาตรา 797–828"],
                ["แบบสำหรับนิติกรรมที่ดิน", "หนังสือมอบอำนาจ แบบ ท.ด.21 ของกรมที่ดิน"],
                ["อากรแสตมป์", "ตามบัญชีอัตราอากรแสตมป์ ท้ายประมวลรัษฎากร (กรมสรรพากร)"],
                ["ผู้รับรองลายมือชื่อในไทย", "ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (สภาทนายความ)"],
                ["รับรองเพื่อใช้ต่างประเทศ", "กองสัญชาติและนิติกรณ์ กรมการกงสุล แล้วสถานทูตปลายทาง"],
                ["Apostille กับประเทศไทย", "มีผลใช้บังคับวันที่ 28 กุมภาพันธ์ 2570"],
                ["เอกสารจากต่างประเทศ", "ต้องแปลเป็นไทยและรับรองคำแปลก่อนยื่นต่อหน่วยงานไทย"],
                ["สิ้นผลเมื่อ", "เพิกถอน ทำการเสร็จสิ้น ผู้มอบอำนาจตาย หรือถูกสั่งเป็นคนไร้ความสามารถ"],
              ].map(([k, v]) => (
                <tr key={k} className="border-b">
                  <th scope="row" className="w-1/3 py-2 pr-3 text-left align-top font-semibold">{k}</th>
                  <td className="py-2 align-top text-muted-foreground">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {POA_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/poa/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="ประเทศปลายทาง" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">ลำดับรับรองตามประเทศปลายทาง</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {POA_JURISDICTIONS.map((j) => (
              <a key={j.slug} href={`/poa/jurisdiction/${j.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">
                {j.nameTh}
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_TH} locale="th" />
        <ServiceCautionBlock items={CAUTIONS_TH} locale="th" />

        <section aria-label="คลัสเตอร์ที่เกี่ยวข้อง" className="mt-10">
          <h2 className="mb-3 text-xl font-bold">อ่านต่อในเรื่องที่เกี่ยวข้อง</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            <a href="/notary/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ ทนายรับรองลายมือชื่อ</a>
            <a href="/consular-legalization/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ นิติกรณ์กงสุล/Apostille</a>
            <a href="/property/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ อสังหาริมทรัพย์</a>
            <a href="/inheritance/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ มรดกและพินัยกรรม</a>
            <a href="/corporate-docs/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ เอกสารนิติบุคคล</a>
            <a href="/translation/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ แปลเอกสารรับรอง</a>
          </div>
        </section>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้เราร่างและจัดลำดับรับรองหนังสือมอบอำนาจให้</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งวัตถุประสงค์ หน่วยงานผู้รับ และประเทศปลายทาง ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
            เจ้าหน้าที่จะตรวจแบบฟอร์ม ขอบเขตอำนาจ อากรแสตมป์ และลำดับรับรองให้ก่อนนัดลงนาม
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="poa" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}